/**
 * 좌측 "설비 노드 검색" 패널 (디자인): 검색 · 상태별 개수 4칸 · 상태/타입 필터 칩 · 노드 트리.
 * 트리: 데이터 소스(설비) › 공장 › 설비 행(상태 점 · 이름 · shots/min), 그 아래 처리 단계 · 저장소 · 출력.
 * 오류·경고 행은 상태색 글자 + 옅은 배경 + 왼쪽 막대. 행 클릭 → store.requestFocusNode(id) (선택 + 캔버스 이동).
 */
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { FACTORIES } from "../../data/machines";
import { STATUS_LABEL_KO, NODE_TYPE_LABEL_KO, formatThroughput } from "../../lib/format";
import { filterNodes, selectActions, selectFilters, selectMode, selectNodes, selectSelectedNodeId, usePipelineStore, } from "../../store/pipeline-store";
import { STATUS_ORDER } from "../flow/flow-constants";
import { useDebouncedCallback } from "./hooks";

const TYPE_CHIPS = ["source", "processor", "storage", "output"];
const TYPE_ICON = { source: "precision_manufacturing", processor: "settings", model: "settings", storage: "database", output: "monitor" };

/** 상단 4칸: 정상(정상+수집 중) · 주의(경고+복구 중) · 오류 · 정지 */
function countNodes(nodes) {
    const c = { ok: 0, warn: 0, error: 0, offline: 0 };
    for (const n of nodes) {
        const s = n.data.status;
        if (s === "healthy" || s === "running")
            c.ok++;
        else if (s === "warning" || s === "recovering")
            c.warn++;
        else if (s === "error")
            c.error++;
        else
            c.offline++;
    }
    return c;
}

const NodeRow = memo(function NodeRow({ node, selected, onSelect }) {
    const d = node.data;
    return (<li>
      <button type="button" className={`dfv-tree__row${selected ? " is-selected" : ""}`} data-status={d.status} onClick={() => onSelect(node.id)} aria-current={selected ? "true" : undefined} title={`${d.label} · ${STATUS_LABEL_KO[d.status]}`}>
        <i className="dfv-tree__dot" aria-hidden="true"/>
        <span className="material-symbols-outlined dfv-tree__icon" aria-hidden="true">{TYPE_ICON[d.nodeType] ?? "circle"}</span>
        <span className="dfv-tree__name">{d.label}</span>
        <span className="dfv-tree__value">{formatThroughput(d.throughput, d.nodeType)}</span>
      </button>
    </li>);
});

function Group({ title, count, open, onToggle, children, level = 0 }) {
    return (<section className={`dfv-tree__group dfv-tree__group--l${level}`}>
      <button type="button" className="dfv-tree__head" onClick={onToggle} aria-expanded={open}>
        <span className="material-symbols-outlined" aria-hidden="true">{open ? "expand_more" : "chevron_right"}</span>
        <span className="dfv-tree__title">{title}</span>
        <span className="dfv-tree__count">{count}</span>
      </button>
      {open && children}
    </section>);
}

export const SourceSidebar = memo(function SourceSidebar() {
    const nodes = usePipelineStore(selectNodes);
    const filters = usePipelineStore(selectFilters);
    const selectedId = usePipelineStore(selectSelectedNodeId);
    const mode = usePipelineStore(selectMode);
    const { setFilters, resetFilters, requestFocusNode } = usePipelineStore(selectActions);

    // 검색 입력 디바운스
    const [query, setQuery] = useState(filters.query);
    const pushQuery = useDebouncedCallback((q) => setFilters({ query: q }), 200);
    useEffect(() => {
        if (filters.query === "")
            setQuery("");
    }, [filters.query]);

    const [open, setOpen] = useState({ source: true, processor: false, storage: false, output: false });
    const toggle = useCallback((k) => setOpen((o) => ({ ...o, [k]: !(o[k] ?? true) })), []);
    const isOpen = (k) => open[k] ?? true;

    const filtered = useMemo(() => filterNodes(nodes, filters), [nodes, filters]);
    const counts = useMemo(() => countNodes(nodes), [nodes]);
    const grouped = useMemo(() => {
        const byFactory = Object.fromEntries(FACTORIES.map((f) => [f.id, []]));
        const processor = [];
        const storage = [];
        const output = [];
        for (const n of filtered) {
            switch (n.data.nodeType) {
                case "source":
                    if (n.data.factoryId && byFactory[n.data.factoryId])
                        byFactory[n.data.factoryId].push(n);
                    break;
                case "processor":
                case "model":
                    processor.push(n);
                    break;
                case "storage":
                    storage.push(n);
                    break;
                case "output":
                    output.push(n);
                    break;
                default:
                    break;
            }
        }
        return { byFactory, processor, storage, output };
    }, [filtered]);

    const toggleIn = (key, value) => setFilters({
        [key]: filters[key].includes(value) ? filters[key].filter((x) => x !== value) : [...filters[key], value],
    });
    const hasFilter = filters.query !== "" || filters.statuses.length > 0 || filters.types.length > 0;
    const loading = mode === "idle" && nodes.length === 0;
    const sourceCount = FACTORIES.reduce((a, f) => a + (grouped.byFactory[f.id]?.length ?? 0), 0);
    const rows = (list) => (<ul className="dfv-tree__rows">
        {list.map((n) => <NodeRow key={n.id} node={n} selected={n.id === selectedId} onSelect={requestFocusNode}/>)}
      </ul>);

    return (<aside className="dfv-panel dfv-side" aria-label="설비 노드 검색">
      <h2 className="dfv-panel__title">설비 노드 검색</h2>

      <label className="dfv-search">
        <span className="material-symbols-outlined" aria-hidden="true">search</span>
        <input type="search" value={query} placeholder="설비 노드 검색 (예: 01호기, M12)" aria-label="설비 노드 검색" onChange={(e) => { setQuery(e.target.value); pushQuery(e.target.value); }}/>
      </label>

      <div className="dfv-counts" aria-label="상태별 노드 수">
        {[
            ["정상", counts.ok, "is-good", "정상 + 수집 중"],
            ["주의", counts.warn, "is-warn", "경고 + 복구 중"],
            ["오류", counts.error, "is-bad", "오류"],
            ["정지", counts.offline, "is-off", "정지 (데이터 없음 포함)"],
        ].map(([label, n, tone, tip]) => (<div key={label} className={`dfv-count ${tone}`} title={tip}>
            <b>{loading ? "—" : n}</b>
            <span>{label}</span>
          </div>))}
      </div>

      <div className="dfv-chips" role="group" aria-label="상태·타입 필터">
        {STATUS_ORDER.map((s) => (<button key={s} type="button" className={`dfv-chip${filters.statuses.includes(s) ? " is-active" : ""}`} data-status={s} aria-pressed={filters.statuses.includes(s)} onClick={() => toggleIn("statuses", s)} title={`상태 필터: ${STATUS_LABEL_KO[s]} (여러 개 선택 가능)`}>
            {STATUS_LABEL_KO[s]}
          </button>))}
        {TYPE_CHIPS.map((t) => (<button key={t} type="button" className={`dfv-chip dfv-chip--type${filters.types.includes(t) ? " is-active" : ""}`} aria-pressed={filters.types.includes(t)} onClick={() => toggleIn("types", t)} title={`타입 필터: ${NODE_TYPE_LABEL_KO[t]} (여러 개 선택 가능)`}>
            {NODE_TYPE_LABEL_KO[t]}
          </button>))}
        {hasFilter && (<button type="button" className="dfv-chip dfv-chip--reset" onClick={resetFilters} title="필터 초기화">
            <span className="material-symbols-outlined" aria-hidden="true">restart_alt</span>초기화
          </button>)}
      </div>

      <div className="dfv-tree dfv-scroll">
        {loading ? (<div className="dfv-empty">노드 목록 불러오는 중…</div>)
            : filtered.length === 0 ? (<div className="dfv-empty">{nodes.length === 0 ? "표시할 노드가 없습니다." : "조건에 맞는 노드가 없습니다."}</div>)
            : (<>
            <Group title="데이터 소스 (설비)" count={sourceCount} open={isOpen("source")} onToggle={() => toggle("source")}>
              {FACTORIES.map((f) => {
                    const list = grouped.byFactory[f.id] ?? [];
                    if (list.length === 0)
                        return null;
                    const key = `f:${f.id}`;
                    return (<Group key={f.id} level={1} title={f.name} count={list.length} open={isOpen(key)} onToggle={() => toggle(key)}>
                    {rows(list)}
                  </Group>);
                })}
            </Group>
            {grouped.processor.length > 0 && (<Group title="처리 단계" count={grouped.processor.length} open={isOpen("processor")} onToggle={() => toggle("processor")}>
                {rows(grouped.processor)}
              </Group>)}
            {grouped.storage.length > 0 && (<Group title="저장소" count={grouped.storage.length} open={isOpen("storage")} onToggle={() => toggle("storage")}>
                {rows(grouped.storage)}
              </Group>)}
            {grouped.output.length > 0 && (<Group title="출력" count={grouped.output.length} open={isOpen("output")} onToggle={() => toggle("output")}>
                {rows(grouped.output)}
              </Group>)}
          </>)}
      </div>
    </aside>);
});
export default SourceSidebar;
